import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormInput from '../components/ui/FormInput';
import { useRealtorContext } from '../context/RealtorContext';
import toast from 'react-hot-toast';

const InviteAffiliatePage: React.FC = () => {
  const { currentUser, loading, error } = useRealtorContext();
  const [affiliateLink, setAffiliateLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      // In a real application, this link would be generated by the backend
      // and include a unique referral ID for the current realtor.
      // For this demo, we'll use a placeholder.
      const baseUrl = window.location.origin;
      setAffiliateLink(`${baseUrl}/affiliate/register?referredBy=${currentUser.id}`);
    }
  }, [currentUser]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    toast.success('Affiliate link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
        <p className="text-lg text-neutral-700">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
        <Navbar />
        <main className="flex-grow pt-20 flex items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-neutral-700">{error}</p>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
        <Navbar />
        <main className="flex-grow pt-20 flex items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold text-primary-800 mb-4">Access Denied</h2>
            <p className="text-neutral-700">Please log in to access this page.</p>
            <Button onClick={() => window.location.href = '/login'} className="mt-6">Go to Login</Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-24 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-secondary-500/10 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
                <span className="w-2 h-2 bg-accent-400 rounded-full animate-pulse-soft" />
                <span className="text-sm font-medium">Expand Your Network</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-white to-secondary-200 bg-clip-text text-transparent">
                  Invite New
                </span>
                <br />
                <span className="bg-gradient-to-r from-secondary-300 to-secondary-500 bg-clip-text text-transparent">
                  Affiliates
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed">
                Share your unique affiliate link and earn commissions from your downliners' sales.
              </p>
            </div>
          </div>
        </div>
        
        {/* Invitation Section */}
        <Section className="py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-display font-bold text-primary-800 mb-4">
                Your Affiliate Link
              </h2>
              <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
                Copy and share this link with potential affiliates to grow your network.
              </p>
            </div>

            <Card className="p-8 lg:p-12" shadow="large" glass>
              <div className="space-y-6">
                <FormInput
                  label="Your Unique Affiliate Link"
                  name="affiliateLink"
                  value={affiliateLink}
                  readOnly
                  onClick={handleCopyLink}
                  className="cursor-pointer focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                  helperText="Click the input field to copy the link"
                />
                <Button
                  onClick={handleCopyLink}
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={copied}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m-4 3H9m10 0a2 2 0 012 2v2a2 2 0 01-2 2H9a2 2 0 01-2-2v-2a2 2 0 012-2h10zm-4.5-4.5l-2.5 2.5-2.5-2.5" />
                    </svg>
                  }
                  iconPosition="right"
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
              </div>
            </Card>
          </div>
        </Section>

        {/* How It Works Section */}
        <Section className="py-20 bg-gradient-to-br from-primary-50 via-white to-secondary-50/30">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-primary-800 mb-4">
              How Our Affiliate Program Works
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              A simple guide to expanding your network and maximizing your earnings.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow-secondary">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">1. Share Your Link</h3>
              <p className="text-neutral-600 leading-relaxed">
                Distribute your unique affiliate link to potential realtors and clients interested in solar properties.
              </p>
            </Card>
            
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">2. Affiliates Join</h3>
              <p className="text-neutral-600 leading-relaxed">
                When someone registers using your link, they become your direct (Level 1) affiliate.
              </p>
            </Card>
            
            <Card hover className="p-8 text-center" shadow="medium">
              <div className="w-16 h-16 bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-primary-800 mb-4">3. Earn Commissions</h3>
              <p className="text-neutral-600 leading-relaxed">
                Receive commissions on sales made by your direct affiliates and their referrals (Level 2).
              </p>
            </Card>
          </div>
        </Section>
      </main>
      
      <Footer />
    </div>
  );
};

export default InviteAffiliatePage;
